import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

export const getRouter = () => {
  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    // Never rewrite the address bar (e.g. adding/removing a trailing slash),
    // which would drop the query string before trackers read it.
    trailingSlash: "preserve",
    defaultPreloadStaleTime: 0,
  });

  // Server only: slow request trace marks for the router phases between the
  // route loaders and the first React render. A hang on 1 Oct 2026 stopped
  // after the blog loader and before React started. The global exists only on
  // the server (src/lib/request-trace.ts).
  const trace = (globalThis as { __rhMark?: (name: string) => void }).__rhMark;
  if (trace) {
    // Mark each route chunk load the router waits on after the loaders.
    // The hang on 1 Oct 2026 stops after loader.done and before
    // ssr.dehydrate, where router.load() awaits these chunk promises.
    const routes = (router as unknown as { routesById: Record<string, { id: string; options: Record<string, unknown> }> }).routesById;
    for (const route of Object.values(routes)) {
      for (const key of ["component", "errorComponent", "pendingComponent", "notFoundComponent"]) {
        const comp = route.options[key] as { preload?: () => unknown; __rhWrapped?: boolean } | undefined;
        if (!comp || typeof comp.preload !== "function" || comp.__rhWrapped) continue;
        const original = comp.preload;
        comp.__rhWrapped = true;
        comp.preload = () => {
          const g = (globalThis as { __rhMark?: (name: string) => void }).__rhMark;
          g?.(`chunk ${route.id} ${key}`);
          const result = original();
          if (result && typeof (result as Promise<unknown>).then === "function") {
            return (result as Promise<unknown>).then((value) => {
              g?.(`chunk.done ${route.id} ${key}`);
              return value;
            });
          }
          return result;
        };
      }
    }
    router.subscribe("onLoad", () => trace("router.onLoad"));
    router.subscribe("onResolved", () => trace("router.onResolved"));
    let ssr: unknown;
    Object.defineProperty(router, "serverSsr", {
      configurable: true,
      enumerable: true,
      get: () => ssr,
      set: (value: { dehydrate?: (...args: unknown[]) => Promise<unknown> } | undefined) => {
        if (value && typeof value.dehydrate === "function") {
          const dehydrate = value.dehydrate.bind(value);
          value.dehydrate = async (...args: unknown[]) => {
            trace("ssr.dehydrate");
            const result = await dehydrate(...args);
            trace("ssr.dehydrated");
            return result;
          };
        }
        ssr = value;
      },
    });
  }

  return router;
};
